import { appManager, AppState } from '../manager';
import {
  arrayToIndexedObject,
  IndexedObject,
  makeAPIProp,
} from 'services/indexedObject';

import * as types from '../types';
import {
  CBRequest,
  CDSContractType,
  HitCBResponseType,
  InterBankDeal,
  MetricsSocketResult,
  MRRHistoryType,
  PlayerAllocationsByInvestmentProfile,
  PlayerHistorySocketResult,
  RFQRequest,
  RFQResponse,
  TrainerPlayersTableUpdates,
  Wallet,
} from '../types';
import { notification } from 'antd';
import { formatters } from 'services';
import { OptionsChainRow } from '../../../views/TraineeSession/OptionsTab/OptionsChain';
import app from '../../../App';
import { CB_DEAL_REJECTED } from '../constants';
import { mergeAFSDV01History } from './afsDv01History';
import { currentPlayerData, updateOrQueuePlayer } from './pendingUpdates';
import { mergeValuation } from './valuation';

const applyPlayerValuation = (state: AppState, result: types.PlayerValuation) => {
  if (
    state.active_session !== result.session_id &&
    state.retrieving_session !== result.session_id
  ) return;
  const { player_id, session_id: _sessionId, ...summary } = result;
  if (state.player.item.id === player_id) {
    Object.assign(state.player.item, mergeValuation(state.player.item, summary));
  } else if (
    state.player.item.id === undefined ||
    state.retrieving_session === result.session_id
  ) {
    updateOrQueuePlayer(state, player_id, summary);
  }
};

export const playerValuationRefreshed = appManager.createLocalEvent(
  'PLAYER_VALUATION_REFRESHED',
  applyPlayerValuation,
);

appManager.createSocketListener<types.PlayerValuation, AppState>(
  'player_summary_updated',
  applyPlayerValuation,
);
// import app from '../../../App';

export const updateInvestmentProfileOld = appManager.createApi<
  { data: types.InvestmentProfile },
  types.InvestmentProfile,
  AppState
>('UPDATE_INVESTMENT_PROFILE_OLD', {
  path: '/players/update_investment_profile',
  method: 'POST',
  successReducer(state, result) {
    state.player.item.investment_profile = result;
  },
});
export const makeOrBreakDeposit = appManager.createApi<
  {
    wallet_id: number;
    deposit: number;
  },
  types.Wallet,
  AppState
>('MAKE_OR_BREAK_DEPOSIT', {
  path: '/wallets/:wallet_id/make_or_break_deposit',
  method: 'POST',
  successReducer(state, result) {
    const index = state.player.item.wallets.findIndex(
      item => item.id === result.id
    );
    state.player.item.wallets[index] = result;
  },
});

export const exchangeMoney = appManager.createApi<
  { from_wallet: number; to_wallet: number; amount: number },
  { wallets: types.PartialWallet[]; exchange_history: types.ExchangeHistory },
  AppState
>('EXCHANGE_MONEY', {
  path: '/wallets/exchange',
  method: 'POST',
  successReducer(state, result) {
    result.wallets.forEach(wallet => {
      const index = state.player.item.wallets.findIndex(
        item => item.id === wallet.id
      );

      state.player.item.wallets[index].amount = wallet.amount;
    });
    const player = state.player.item;
    if (!player.exchange_history) player.exchange_history = [];

    player.exchange_history.push(result.exchange_history);
  },
});

export const updateSAL = appManager.createApi<
  types.UpdateSALPayload,
  types.StrategicAssetAllocation[],
  AppState
>('UPDATE_SAL', {
  path: '/players/set_strategic_asset_allocations',
  method: 'POST',
  successReducer(state, result) {
    state.player.item.strategic_asset_allocations = result;
  },
});

export const updatePlayerSettings = appManager.createApi<
  { name: string },
  { name: string },
  AppState
>('UPDATE_PLAYER_Settings', {
  path: '/players/update_settings',
  method: 'POST',
  successReducer(state, result) {
    state.player.item = { ...state.player.item, ...result };
  },
});

export const fetchBlotter = appManager.createApi<
  unknown,
  {
    orders: types.Order[];
    exchange_history: types.ExchangeHistory[];
    deposits_history: types.DepositHistory[];
  },
  AppState
>('FETCH_BLOTTER', {
  path: '/players/get_blotter',
  method: 'GET',
  startReducer(state) {
    state.player.item.fetching_blotter = true;
    state.player.item.fetched_blotter = false;
    state.orders.fetching = true;
  },
  successReducer(state, result) {
    state.orders = makeAPIProp(result.orders);
    state.player.item.exchange_history = result.exchange_history;
    state.player.item.deposits_history = result.deposits_history;
    state.player.item.fetching_blotter = false;
    state.player.item.fetched_blotter = true;
    state.orders.fetching = false;
  },
  failReducer(state) {
    state.player.item.fetching_blotter = false;
    state.orders.fetching = false;
  },
});

export const fetchRepoContracts = appManager.createApi<
  { session_id: number },
  CDSContractType[],
  AppState
>('FETCH_REPO_CONTRACTS', {
  path: '/players/get_repo_contracts',
  method: 'POST',
  startReducer(state) {
    // initialize
    state.player.item.repo_contracts = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.player.item.repo_contracts.items = arrayToIndexedObject(result);
    state.player.item.repo_contracts.fetching = false;
    state.player.item.repo_contracts.fetched = true;
  },
  failReducer(state) {
    state.player.item.repo_contracts.fetching = false;
    state.player.item.repo_contracts.fetched = false;
  },
});

export const fetchCDSContracts = appManager.createApi<
  { session_id: number },
  CDSContractType[],
  AppState
>('FETCH_CDS_CONTRACTS', {
  path: '/players/get_cds_contracts',
  method: 'POST',
  startReducer(state) {
    // initialize
    state.player.item.cds_contracts = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    state.player.item.cds_contracts.items = arrayToIndexedObject(result);
    state.player.item.cds_contracts.fetching = false;
    state.player.item.cds_contracts.fetched = true;
  },
  failReducer(state) {
    state.player.item.cds_contracts.fetching = false;
    state.player.item.cds_contracts.fetched = false;
  },
});

export const fetchForwardContracts = appManager.createApi<
  { session_id: number },
  {
    fwd_contracts: types.ForwardContract[];
    fwd_odas: types.OdaForward[];
  },
  AppState
>('FETCH_FWD_CONTRACTS', {
  path: '/players/get_fwd_contracts',
  method: 'POST',
  startReducer(state) {
    // initialize
    state.player.item.forward_contracts = {
      items: {},
      fetching: true,
      fetched: false,
    };
    state.player.item.forward_oda = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    // state.player.item.forward_contracts = makeAPIProp(result.fwd_contracts);
    state.player.item.forward_contracts.items = arrayToIndexedObject(
      result.fwd_contracts
    );
    state.player.item.forward_contracts.fetching = false;
    state.player.item.forward_contracts.fetched = true;

    // state.player.item.forward_oda = makeAPIProp(result.fwd_odas);
    state.player.item.forward_oda.items = arrayToIndexedObject(result.fwd_odas);
    state.player.item.forward_oda.fetching = false;
    state.player.item.forward_oda.fetched = true;
  },
  failReducer(state) {
    state.player.item.forward_contracts.fetching = false;
    state.player.item.forward_contracts.fetched = false;

    state.player.item.forward_oda.fetching = false;
    state.player.item.forward_oda.fetched = false;
  },
});

export const fetchForwardContractsByPlayerId = appManager.createApi<
  { player_id: number },
  {
    fwd_contracts: types.ForwardContract[];
    player_id_received: number;
  },
  AppState
>('FETCH_FWD_CONTRACTS_BY_PLAYER_ID', {
  path: '/players/get_fwd_contracts_by_player_id',
  method: 'POST',
  startReducer(state, payload) {
    // initialize
    state.players.items[payload.player_id].forward_contracts = {
      items: {},
      fetching: true,
      fetched: false,
    };
  },
  successReducer(state, result, payload) {
    state.players.items[payload.player_id].forward_contracts.items =
      arrayToIndexedObject(result.fwd_contracts);
    state.players.items[payload.player_id].forward_contracts.fetching = false;
    state.players.items[payload.player_id].forward_contracts.fetched = true;
  },
  failReducer(state, error, payload) {
    state.players.items[payload.player_id].forward_contracts.fetching = false;
    state.players.items[payload.player_id].forward_contracts.fetched = false;
  },
});

// export const invalidateForwardContracts = appManager.createLocalEvent(
//   'invalidate_forward_contracts',
//   (state, payload) => {
//     state.forward_contracts.fetching = false;
//     state.forward_contracts.fetched = false;
//     state.forward_contracts.items = undefined;
//
//   }
// );

export const initializeForwardContractsState = appManager.createLocalEvent(
  'initialize_forward_contracts_state',
  (state, payload) => {
    const player = state.players.items[payload.player_id];
    if (player?.forward_contracts?.fetched) {
      // don't initialize with no data if data already present
    } else {
      state.players.items[payload.player_id].forward_contracts = {
        items: {},
        fetching: true,
        fetched: false,
      };
    }
  }
);

appManager.createSocketListener<
  { player_id: number; allocations: types.TacticalAssetAllocation[] },
  AppState
>('tactical_asset_allocation_received', (state, result) => {
  if (state.player.item.id === undefined) {
    // trainer
    updateOrQueuePlayer(state, result.player_id, {
      tactical_asset_allocations: result.allocations,
    });
  } else {
    // player
    state.player.item.tactical_asset_allocations = result.allocations;
  }
});

appManager.createSocketListener<types.RegionAllocation[], AppState>(
  'region_allocations_received',
  (state, result) => {
    state.player.item.region_allocations = result;
  }
);

appManager.createSocketListener<types.MetricsSocketResult, AppState>(
  'metrics_received',
  (state, result) => {
    // console.log(result);
    if (state.player.item.id === undefined) {
      // CASE: window is in trainer view
      const playerId = result.player;
      const { player: _playerId, ...metrics } = result;
      updateOrQueuePlayer(state, playerId, {
        metrics: [
          ...(currentPlayerData(state, playerId)?.metrics ?? []),
          metrics,
        ],
        last_metrics: metrics,
      });
    } else {
      // CASE: window is in trainee view
      if (result.player === state.player.item.id) {
        state.player.item.metrics?.push(result);
      }
    }
  }
);

appManager.createSocketListener<types.PlayerBadge, AppState>(
  'badge_unlocked',
  (state, result) => {
    state.player.item.badges.push(result);
    const badge = state.badges.items[result.badge];

    state.player.item.coins += badge.coins;
  }
);

appManager.createSocketListener<
  {
    market_value: number;
    remaining_short_sell_amount_limit: number;
    remaining_fx_amount_limit: number;
    risk_weighted_value: number;
    book_value: number;
    portfolio_return: number;
    fx_return: number;
    fx_return_percent: number;
    htm_portfolio_dv01: number;
    htm_portfolio_duration: number;
    afs_portfolio_dv01: number;
    afs_portfolio_duration: number;
    latest_afs_dv01?: types.LatestAFSDV01 | null;
    valuation_at?: string | null;
  },
  AppState
>('portfolio_value_updated', (state, result) => {
  // console.log('portfolio_value_updated: ', result);
  state.player.item = {
    ...state.player.item,
    ...mergeValuation(state.player.item, result),
  };
  if (result.latest_afs_dv01) {
    state.player.item.afs_dv01_history = mergeAFSDV01History(
      state.player.item.afs_dv01_history,
      [result.latest_afs_dv01],
    );
  }
});

appManager.createSocketListener<types.PlayerHistorySocketResult, AppState>(
  'player_history_created',
  (state, result) => {
    if (result.summary) applyPlayerValuation(state, result.summary);
    // console.log('player_history_created: ', result);
    if (state.player.item.id === undefined) {
      // CASE: window is in trainer view
      const playerId = result.player;
      const { player: _playerId, summary: _summary, ...history } = result;
      updateOrQueuePlayer(state, playerId, {
        player_history: [
          ...(currentPlayerData(state, playerId)?.player_history ?? []),
          history,
        ],
      });
    } else {
      // CASE: window is in trainee view
      if (result.player === state.player.item.id) {
        state.player.item.player_history?.push(result);
      }
    }
  }
);

// Predetermined-intervals display fills: ONE frame per just-closed gap, batched as a map
// {gap: [d_k, d_next], players: {playerId: [points]}} (unlike the point-per-frame
// metrics_received). Appends unconditionally (no currently_passing_time gating, same as the
// other listeners) and lazy-inits display_series so a fill frame arriving before the hydration
// fetch/retrieve completes is never dropped. Fixed/RT sessions never receive this event.
appManager.createSocketListener<types.DisplaySeriesFilledResult, AppState>(
  'display_series_filled',
  (state, result) => {
    if (state.player.item.id === undefined) {
      // CASE: window is in trainer view -- fills arrive for every player
      Object.entries(result.players).forEach(([playerId, points]) => {
        updateOrQueuePlayer(state, +playerId, {
          display_series: [
            ...(currentPlayerData(state, +playerId)?.display_series ?? []),
            ...points,
          ],
        });
      });
    } else {
      // CASE: window is in trainee view -- keep only own fills
      const ownPoints = result.players[String(state.player.item.id)];
      if (ownPoints) {
        if (!state.player.item.display_series) state.player.item.display_series = [];
        state.player.item.display_series.push(...ownPoints);
      }
    }
  }
);

appManager.createSocketListener<types.TrainerPlayersTableUpdates[], AppState>(
  'trainer_receive_players_table_updates',
  (state, result) => {
    if (state.player.item.id === undefined) {
      // CASE: window is in trainer view
      // console.log('trainer_updates result: ', result);
      result.forEach(player => {
        // console.log('player.id: ', player.id);
        const prev = currentPlayerData(state, player.id);
        const afsDv01History = mergeAFSDV01History(
          prev?.afs_dv01_history,
          player.latest_afs_dv01 ? [player.latest_afs_dv01] : undefined,
        );
        updateOrQueuePlayer(state, player.id, {
          last_portfolio_return: player.last_portfolio_return,
          last_market_value: player.last_market_value,
          last_pnl_sum: player.last_pnl_sum,
          last_fx_return: player.last_fx_return,
          latest_afs_dv01: player.latest_afs_dv01,
          afs_dv01_history: afsDv01History,
          // deals: player.deals,
        });
      });
      // const playerId = result.player;
      // delete result.player;
      // state.players.items[playerId]?.player_history?.push(result);
    }
  }
);

appManager.createSocketListener<{ id: number }, AppState>(
  'player_connected',
  (state, result) => {
    const player = state.players.items[result.id];
    if (!player) return;
    player.online = true;
  }
);

appManager.createSocketListener<{ id: number }, AppState>(
  'player_disconnected',
  (state, result) => {
    const player = state.players.items[result.id];
    if (!player) return;
    player.online = false;
  }
);

export const updatePlayerBenchmarkComposition = appManager.createApi<
  { composition?: types.BenchmarkComposition[]; player_id: number },
  { composition: types.BenchmarkComposition[] },
  AppState
>('UPDATE_PLAYER_BENCHMARK_COMPOSITION', {
  path: '/players/:player_id/update_benchmark_composition',
  method: 'POST',
  successReducer(state, result) {
    state.player.item.benchmark_compositions = result.composition;
  },
});

appManager.createSocketListener<
  {
    financial_instruments: {
      id: number;
      pv_limit: number;
      risk_weight: number;
    }[];
  },
  AppState
>('update_instruments_options', (state, result) => {
  const instruments = state.player.item.financial_instruments;
  result.financial_instruments.forEach(({ id, risk_weight, pv_limit }) => {
    instruments[id].risk_weight = risk_weight;
    instruments[id].pv_limit = pv_limit;
  });
});

appManager.createSocketListener<
  {
    fins: {
      id: number;
      reporting_realized_pnl: number;
      reporting_unrealized_pnl: number;
      amount: number;
      available_repoable_amount: number;
      repoed_amount: number;
      live_bond_market_visible?: boolean;
      simplified_option?: types.SimplifiedOptionInstrumentState;
    }[];
  },
  // unknown,
  // UpdateFins[],
  AppState
>('update_instruments', (state, result) => {
  // console.log('update_instruments results:', result);
  // const instruments = state.player.item.financial_instruments;
  // console.log('UPDATE_INSTRUMENTS result', result);
  // result?.fins?.forEach(
  //   ({
  //     id,
  //     reporting_realized_pnl,
  //     reporting_unrealized_pnl,
  //     amount,
  //     available_repoable_amount,
  //   }) => {
  //     instruments[id].reporting_realized_pnl = reporting_realized_pnl;
  //     instruments[id].reporting_unrealized_pnl = reporting_unrealized_pnl;
  //     instruments[id].amount = amount;
  //     instruments[id].available_repoable_amount = available_repoable_amount;
  //   }
  // );

  const instruments = state.player.item.financial_instruments;
  // console.log('UPDATE_INSTRUMENTS result', result);

  const updatedInstruments = { ...instruments };

  // Update each instrument in the shallow copy
  result?.fins?.forEach(
    ({
      id,
      reporting_realized_pnl,
      reporting_unrealized_pnl,
      amount,
      available_repoable_amount,
      repoed_amount,
      live_bond_market_visible,
      simplified_option,
    }) => {
      // Lifecycle hydration supplies complete incoming instruments. A delayed
      // partial update cannot recreate one that hydration deliberately hid.
      if (!instruments[id] && state.sessions.items[state.active_session]?.live_synthetic_bonds_enabled) return;
      updatedInstruments[id] = {
        ...instruments[id],
        reporting_realized_pnl,
        reporting_unrealized_pnl,
        amount,
        available_repoable_amount,
        repoed_amount,
        ...(live_bond_market_visible !== undefined ? {
          live_bond_market_visible: instruments[id]?.live_bond_market_visible === false ? false : live_bond_market_visible,
        } : {}),
        ...(simplified_option !== undefined ? { simplified_option } : {}),
      };
    }
  );

  // Mutate the state by assigning the updated instruments
  state.player.item.financial_instruments = updatedInstruments;
});

appManager.createSocketListener<
  Wallet[],
  // unknown,
  // UpdateFins[],
  AppState
>('update_wallets', (state, result) => {
  const wallets = state.player.item.wallets;
  // console.log('wallets result: ', result);
  result.forEach(updated_wallet => {
    const wallet = wallets.find(wallet => wallet.id === updated_wallet.id);
    if (wallet !== undefined) {
      wallet.amount = updated_wallet.amount;
      wallet.collateral_amount = updated_wallet.collateral_amount;
      wallet.client_money = updated_wallet.client_money;
      wallet.client_interest = updated_wallet.client_interest;
      wallet.lombard_interest = updated_wallet.lombard_interest;
      wallet.options_net_premiums = updated_wallet.options_net_premiums;
    }
  });
});

appManager.createSocketListener<
  {
    instrument_id: number;
    collateral: number;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('update_instrument_collateral', (state, result) => {
  state.player.item.financial_instruments[result.instrument_id].collateral =
    result.collateral;
});
// commented because this would trigger an S3 file fetch and currently boto3 S3 access is deactivated in backend
export const retrieveInsightsReports = appManager.createApi<
  { player_id: number },
  types.InsightsReport[],
  AppState
>('RETRIEVE_INSIGHTS_REPORTS', {
  path: '/players/:player_id/insights_reports',
  method: 'GET',
  successReducer(state, result) {
    result.forEach(report => {
      state.insightsReports.items[report.id] = report;
    });

    const player = state.player.item;
    player.insights_reports = result.map(r => r.id);
  },
});

// commented because this would trigger an S3 file fetch and currently boto3 S3 access is deactivated in backend
export const listRoadmapItems = appManager.createApi<
  { player_id: number },
  types.RoadmapItem[],
  AppState
>('LIST_ROADMAP_ITEMS', {
  path: '/players/:player_id/roadmap_items',
  method: 'GET',
  successReducer(state, result) {
    result.forEach(item => {
      state.roadmapItems.items[item.id] = item;
    });
  },
});

export const updatePortfolioName = appManager.createApi<
  { player_id: number; portfolio_name: string },
  { portfolio_name: string },
  AppState
>('UPDATE_PORTFOLIO_NAME', {
  path: '/players/:player_id/update_portfolio_name',
  method: 'POST',
  successReducer(state, result) {
    state.player.item.portfolio_name = result.portfolio_name;
    // if (state.players.items[payload.player_id]) {
    //   state.players.items[payload.player_id].portfolio_name =
    //     result.portfolio_name;
    // }
  },
});

export const callCentralBank = appManager.createApi<
  { player_id: number; amount: number },
  { cb_request: CBRequest },
  AppState
>('CALL_CENTRAL_BANK', {
  path: '/players/call_central_bank',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    if (state.player.item.cb_requests === undefined) {
      state.player.item.cb_requests = [];
    }
    state.player.item.cb_requests.push(result.cb_request);
  },
});

export const cancelCentralBankCall = appManager.createApi<
  { player_id: number },
  // { cb_requests: CBRequest[] },
  unknown,
  AppState
>('CANCEL_CENTRAL_BANK_CALL', {
  path: '/players/:player_id/cancel_central_bank_call',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    state.player.item.cb_requests = [];
  },
});

export const sendRFQ = appManager.createApi<
  { amount: number; player_id: number; selected_players: number[] },
  { new_rfq: RFQRequest },
  AppState
>('SEND_RFQ', {
  path: '/players/send_rfq',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    if (state.player.item.sent_rfq_requests === undefined) {
      state.player.item.sent_rfq_requests = [];
    }
    state.player.item.sent_rfq_requests.push(result.new_rfq);
  },
});

export const cancelRFQ = appManager.createApi<
  { request_id: number },
  // { updated_rfq: RFQRequest },
  unknown,
  AppState
>('CANCEL_RFQ', {
  path: '/players/cancel_rfq',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    // const requestIndex = state.player.item.sent_rfq_requests.findIndex(
    //   request => request.id === result.updated_rfq.id
    // );
    // state.player.item.sent_rfq_requests[requestIndex] = result.updated_rfq;
  },
});

export const hitRFQResponse = appManager.createApi<
  { response_id: number; type: number },
  // { updated_rfq: RFQRequest },
  unknown,
  AppState
>('HIT_RFQ_RESPONSE', {
  path: '/players/hit_rfq_response',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    // const requestIndex = state.player.item.sent_rfq_requests.findIndex(
    //   request => request.id === result.updated_rfq.id
    // );
    // state.player.item.sent_rfq_requests[requestIndex] = result.updated_rfq;
  },
});

export const hitCBResponse = appManager.createApi<
  { response_id: number; action: number; player_id: number },
  HitCBResponseType,
  AppState
>('HIT_CB_RESPONSE', {
  path: '/players/:player_id/hit_cb_response',
  method: 'POST',
  successReducer(state, result, payload) {
    if (result.type === CB_DEAL_REJECTED) {
      const index = state.player.item.cb_requests.findIndex(
        req => req.id === result.request_id
      );
      if (index !== -1) {
        state.player.item.cb_requests.splice(index, 1);
      }
      notification.info({ message: result.message });
    } else {
      // CB_DEAL_TAKEN
      const index = state.player.item.cb_requests.findIndex(
        req => req.id === result.request.id
      );
      state.player.item.cb_requests[index] = result.request;

      const wallet_index = state.player.item.wallets.findIndex(
        wallet => wallet.id === result.wallet_id
      );
      if (wallet_index !== -1) {
        state.player.item.wallets[wallet_index].amount = result.wallet_amount;
        state.player.item.wallets[wallet_index].cb_deposit_amount =
          result.wallet_cb_deposit_amount;
      }
      if (result.canceled_ids) {
        result.canceled_ids.forEach(canceled_id => {
          const index = state.player.item.cb_requests.findIndex(
            req => req.id === canceled_id
          );
          if (index !== -1) {
            state.player.item.cb_requests.splice(index, 1);
          }
        });
      }
    }
  },
});

appManager.createSocketListener<
  {
    updated_cb_requests: CBRequest[];
    reporting_wallet: Wallet;
    canceled_ids: number[];
  },
  // unknown,
  // UpdateFins[],
  AppState
>('updated_cb_requests', (state, result) => {
  result.canceled_ids.forEach(canceled_id => {
    const index = state.player.item.cb_requests.findIndex(
      req => req.id === canceled_id
    );
    if (index !== -1) {
      state.player.item.cb_requests.splice(index, 1);
    }
  });
  result.updated_cb_requests.forEach(updated_req => {
    const requestIndex = state.player.item.cb_requests.findIndex(
      request => request.id === updated_req.id
    );
    if (requestIndex !== -1) {
      state.player.item.cb_requests[requestIndex] = updated_req;
    }
  });
  const wallet_index = state.player.item.wallets.findIndex(
    wal => wal.id === result.reporting_wallet.id
  );
  if (wallet_index !== -1) {
    state.player.item.wallets[wallet_index] = result.reporting_wallet;
  }
});

appManager.createSocketListener<
  number[],
  // unknown,
  // UpdateFins[],
  AppState
>('canceled_all_pending_cb_requests', (state, result) => {
  console.log('canceled_all_pending_cb_requests:', result);
  result.forEach(canceled_id => {
    const index = state.player.item.cb_requests.findIndex(
      req => req.id === canceled_id
    );
    if (index !== -1) {
      state.player.item.cb_requests.splice(index, 1);
    }
  });
});

appManager.createSocketListener<
  {
    updated_response: RFQResponse;
    interbank_deal?: InterBankDeal;
    updated_wallet?: Wallet;
    new_mkt_val?: number;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('response_got_hit', (state, result) => {
  if (result.updated_response.status === 'nothing there') {
    notification.info({
      message: `New quote answer: nothing there!`,
      description: `From ${
        result.updated_response.requesting_player_name
      }; Amount: ${formatters.commasNoDigits(result.updated_response.amount)};
      Quote: ${formatters.commasExact2Digits(
        result.updated_response.bid
      )}% | ${formatters.commasExact2Digits(result.updated_response.ask)}%`,
      duration: 5,
    });
  } else if (result.updated_response.status === 'borrow') {
    notification.info({
      message: `Quote hit!`,
      description: `${
        result.updated_response.requesting_player_name
      } borrowed ${formatters.commasNoDigits(
        result.updated_response.amount
      )}; Quote: ${formatters.commasExact2Digits(
        result.updated_response.bid
      )}% | ${formatters.commasExact2Digits(result.updated_response.ask)}%`,
    });
  } else {
    notification.info({
      message: `Quote hit!`,
      description: `${
        result.updated_response.requesting_player_name
      } lent you ${formatters.commasNoDigits(
        result.updated_response.amount
      )}; Quote: ${formatters.commasExact2Digits(
        result.updated_response.bid
      )}% | ${formatters.commasExact2Digits(result.updated_response.ask)}%`,
    });
  }

  // update response
  const responseIndex = state.player.item.rfq_responses.findIndex(
    response => response.id === result.updated_response.id
  );
  state.player.item.rfq_responses[responseIndex] = result.updated_response;

  // add interbank_deal if any
  if (result.interbank_deal) {
    if (state.player.item.interbank_deals === undefined) {
      state.player.item.interbank_deals = [];
    }
    state.player.item.interbank_deals = [
      ...state.player.item.interbank_deals,
      result.interbank_deal,
    ];
  }

  // update wallet if any
  if (result.updated_wallet) {
    const updatedWallet = result.updated_wallet;
    const walletIndex = state.player.item.wallets.findIndex(
      existing_wallet => existing_wallet.id === updatedWallet.id
    );
    state.player.item.wallets[walletIndex] = updatedWallet;
  }

  // update market_value if any
  if (result.new_mkt_val) {
    state.player.item.market_value = result.new_mkt_val;
  }
});

appManager.createSocketListener<
  {
    updated_rfq: RFQRequest;
    interbank_deal?: InterBankDeal;
    updated_wallet?: Wallet;
    new_mkt_val?: number;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('updated_rfq_after_hit', (state, result) => {
  // update request
  const requestIndex = state.player.item.sent_rfq_requests.findIndex(
    request => request.id === result.updated_rfq.id
  );
  state.player.item.sent_rfq_requests[requestIndex] = result.updated_rfq;

  // add interbank_deal if any
  if (result.interbank_deal) {
    if (state.player.item.interbank_deals === undefined) {
      state.player.item.interbank_deals = [];
    }
    state.player.item.interbank_deals = [
      ...state.player.item.interbank_deals,
      result.interbank_deal,
    ];
  }

  // update wallet if any
  if (result.updated_wallet) {
    const updatedWallet = result.updated_wallet;
    const walletIndex = state.player.item.wallets.findIndex(
      existing_wallet => existing_wallet.id === updatedWallet.id
    );
    state.player.item.wallets[walletIndex] = updatedWallet;
  }

  // update market_value if any
  if (result.new_mkt_val) {
    state.player.item.market_value = result.new_mkt_val;
  }
});

appManager.createSocketListener<
  { updated_rfq: RFQRequest },
  // unknown,
  // UpdateFins[],
  AppState
>('canceled_own_rfq', (state, result) => {
  const requestIndex = state.player.item.sent_rfq_requests.findIndex(
    request => request.id === result.updated_rfq.id
  );
  state.player.item.sent_rfq_requests[requestIndex] = result.updated_rfq;
});

appManager.createSocketListener<
  {
    new_interbank_interest: number;
    updated_deals: InterBankDeal[];
  },
  // unknown,
  // UpdateFins[],
  AppState
>('update_interbank_interest', (state, result) => {
  // console.log('update_interbank_interest', result);
  state.player.item.interbank_interest = result.new_interbank_interest;
  state.player.item.interbank_deals = result.updated_deals;
});

appManager.createSocketListener<
  {
    player_id: number;
    mrr_snapshot: MRRHistoryType;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('new_mrr_block', (state, result) => {
  // console.log('update_interbank_interest', result);
  if (state.player.item.id === undefined) {
    // trainer
    state.players.items[result.player_id].mrr_history.push(result.mrr_snapshot);
  } else {
    // player
    state.player.item.mrr_history.push(result.mrr_snapshot);
  }
});

export const sendRFQResponse = appManager.createApi<
  { response_id: number; bid: number; ask: number },
  { locked_in_rfq_response: RFQResponse },
  AppState
>('SEND_RFQ_RESPONSE', {
  path: '/players/send_rfq_response',
  method: 'POST',
  successReducer(state, result) {
    // console.log(result);
    if (state.player.item.rfq_responses === undefined) {
      state.player.item.rfq_responses = [];
    }
    // state.player.item.rfq_responses.push(result.locked_in_rfq_response);
    const responseIndex = state.player.item.rfq_responses.findIndex(
      response => response.id === result.locked_in_rfq_response.id
    );
    state.player.item.rfq_responses[responseIndex] =
      result.locked_in_rfq_response;
  },
});

appManager.createSocketListener<
  {
    response: RFQResponse;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('new_rfq_request_which_awaits_a_response', (state, result) => {
  // console.log('new request received, response: ', result);
  notification.info({
    message: 'New RFQ received!',
    description: `From ${
      result.response.requesting_player_name
    }; Amount: ${formatters.commasNoDigits(result.response.amount)}`,
  }); // TODO: add a more detailed description
  if (state.player.item.rfq_responses === undefined) {
    state.player.item.rfq_responses = [];
  }
  // state.player.item.rfq_responses.push(result.response);
  state.player.item.rfq_responses = [
    ...state.player.item.rfq_responses,
    result.response,
  ];
});

appManager.createSocketListener<
  {
    updated_response: RFQResponse;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('request_for_response_canceled', (state, result) => {
  // console.log('response canceled: ', result);
  notification.info({
    message: `Bank ${
      result.updated_response.requesting_player_name
    } canceled their ${formatters.commasNoDigits(
      result.updated_response.amount
    )} RFQ!`,
  });
  const responseIndex = state.player.item.rfq_responses.findIndex(
    response => response.id === result.updated_response.id
  );

  state.player.item.rfq_responses[responseIndex] = result.updated_response;
});

appManager.createSocketListener<
  {
    updated_request_with_new_response: RFQRequest;
    responding_player_name: string;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('new_rfq_response_locked_in', (state, result) => {
  // console.log('updated_response: ', result);
  notification.info({
    message: `Bank ${
      result.responding_player_name
    } sent a quote for your ${formatters.commasNoDigits(
      result.updated_request_with_new_response.amount
    )} RFQ!`,
  });
  const requestIndex = state.player.item.sent_rfq_requests.findIndex(
    request => request.id === result.updated_request_with_new_response.id
  );

  state.player.item.sent_rfq_requests[requestIndex] =
    result.updated_request_with_new_response;
});

export const fetchPlayerAllocationsByInvestmentProfile = appManager.createApi<
  { player_id: number },
  types.PlayerAllocationsByInvestmentProfile[],
  AppState
>('FETCH_PLAYER_ALLOCATIONS_BY_INVESTMENT_PROFILE', {
  path: '/players/:player_id/fetch_player_allocations_by_investment_profile',
  method: 'GET',
  successReducer(state, result) {
    state.player.item.strategic_group_allocations = result;
    // result.forEach(report => {
    //   state.insightsReports.items[report.id] = report;
    // });
    //
    // const player = state.player.item;
    // player.insights_reports = result.map(r => r.id);
  },
});

export const updatePlayerGroupAllocations = appManager.createApi<
  { allocations: types.AssetGroupAllocationPayload[]; player_id?: number },
  // types.PlayerAllocationsByInvestmentProfile[],
  unknown,
  AppState
>('UPDATE_PLAYER_GROUP_ALLOCATIONS', {
  method: 'POST',
  path: '/players/:player_id/update_player_group_allocations',
  successReducer(state, result) {
    // const player = state.player.item;
    // player.strategic_group_allocations = result;
    // result received on socket so trainer can receive the update as well
  },
});

appManager.createSocketListener<
  types.PlayerAllocationsByInvestmentProfile[],
  // unknown,
  // UpdateFins[],
  AppState
>('player_updated_saa_decision', (state, result) => {
  console.log('player_updated_saa_decision: ', result);
  if (state.player.item.id === undefined) {
    // update meant for trainer
    const player_id = result[0].player;
    state.players.items[player_id].strategic_group_allocations = result;
    const player_name = state.players.items[player_id].name;
    notification.info({
      message: 'New SAA decision!',
      description: `${player_name} updated their Strategic Asset Allocations.`,
    });
  } else {
    state.player.item.strategic_group_allocations = result;
    // update meant for player
  }
});

appManager.createSocketListener<{ busted: boolean }, AppState>(
  'player_went_bust',
  (state, result) => {
    state.player.item.busted = result.busted;
  }
);

appManager.createSocketListener<
  { asset: string; financial_instrument: number; reason?: string },
  AppState
>('futures_position_liquidated', (state, result) => {
  if (result.reason === 'rollover_fee') {
    notification.warning({
      message: 'Futures contract settled',
      description: `Your ${result.asset} position: rollover fee not paid, contract settled.`,
    });
    return;
  }
  notification.warning({
    message: 'Futures position liquidated',
    description: `Your ${result.asset} futures position was auto-liquidated (margin call): its remaining margin fell to or below the maintenance threshold. Any settled loss has been applied to your wallet.`,
  });
});

appManager.createSocketListener<
  { player_id: number; orders_count: number },
  AppState
>('increment_number_of_deals', (state, result) => {
  updateOrQueuePlayer(state, result.player_id, { deals: result.orders_count });
});
